// One shared description of the Softly tube, so the SVG render and the pixel scan always agree.
// Units are the tube's own viewBox (300 x 680). The tube stands upright on its cap.

export type Rgb = [number, number, number]

export type ColorStop = [offset: number, color: string]

export type TubeRegion = 'crimp' | 'body' | 'cap'

export const TUBE_WIDTH = 300
export const TUBE_HEIGHT = 680
export const TUBE_CENTER = 150

export const CRIMP = { left: 30, right: 270, top: 16, bottom: 58 } as const

export const BODY = {
    top: 54,
    taperEnd: 466,
    bottom: 538,
    topHalfWidth: 116,
    taperHalfWidth: 105,
    neckHalfWidth: 68
} as const

export const CAP = { left: 78, right: 222, top: 530, bottom: 664, radius: 10 } as const

// The script face is thin, so the label word is also stroked in white to give it enough weight on the blue.
export const LABEL = { text: 'Softly', x: 150, y: 286, size: 76, weight: 2.6 } as const

// Body outline: a gentle taper from the crimp, then rounded shoulders into the cap.
export const BODY_PATH = 'M34 54 L266 54 L255 466 A37 72 0 0 1 218 538 L82 538 A37 72 0 0 1 45 466 Z'

export const CAP_PATH = 'M78 530 H222 V654 Q222 664 212 664 H88 Q78 664 78 654 Z'

export const DROP_PATH = 'M10 0.8C10 0.8 2 10.4 2 16.1C2 20.6 5.6 24.2 10 24.2C14.4 24.2 18 20.6 18 16.1C18 10.4 10 0.8 10 0.8Z'

export const BODY_STOPS: ColorStop[] = [
    [0, '#1A31A3'],
    [0.07, '#2242D4'],
    [0.2, '#3A66FF'],
    [0.29, '#5A80FF'],
    [0.38, '#3560FA'],
    [0.6, '#2B55F5'],
    [0.82, '#2244D2'],
    [0.94, '#1A35B0'],
    [1, '#2C50DA']
]

export const CRIMP_STOPS: ColorStop[] = [
    [0, '#1C34A8'],
    [0.2, '#3157F0'],
    [0.32, '#4A72FF'],
    [0.6, '#2B55F5'],
    [0.88, '#1E3CC0'],
    [1, '#1A33A6']
]

export const CAP_STOPS: ColorStop[] = [
    [0, '#D6CCC6'],
    [0.14, '#F0EAE6'],
    [0.32, '#FFFFFF'],
    [0.55, '#F7F3F0'],
    [0.8, '#E2D9D3'],
    [1, '#CEC3BC']
]

export const bodyHalfWidth = (y: number): number | null => {
    if (y < BODY.top || y > BODY.bottom) return null

    if (y <= BODY.taperEnd) {
        const t = (y - BODY.top) / (BODY.taperEnd - BODY.top)

        return BODY.topHalfWidth - t * (BODY.topHalfWidth - BODY.taperHalfWidth)
    }

    const k = (y - BODY.taperEnd) / (BODY.bottom - BODY.taperEnd)

    return BODY.neckHalfWidth + (BODY.taperHalfWidth - BODY.neckHalfWidth) * Math.sqrt(Math.max(0, 1 - k * k))
}

export const regionAt = (x: number, y: number): TubeRegion | null => {
    if (y >= CRIMP.top && y <= CRIMP.bottom && x >= CRIMP.left && x <= CRIMP.right) return 'crimp'

    const halfWidth = bodyHalfWidth(y)

    if (halfWidth !== null && Math.abs(x - TUBE_CENTER) <= halfWidth) return 'body'

    if (y >= CAP.top && y <= CAP.bottom && x >= CAP.left && x <= CAP.right) {
        const r = CAP.radius

        if (y > CAP.bottom - r) {
            const cx = Math.min(Math.max(x, CAP.left + r), CAP.right - r)
            const cy = CAP.bottom - r

            if ((x - cx) ** 2 + (y - cy) ** 2 > r * r) return null
        }

        return 'cap'
    }

    return null
}

export const hexToRgb = (hex: string): Rgb => {
    const value = Number.parseInt(hex.replace('#', ''), 16)

    return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

export const mixRgb = (from: Rgb, to: Rgb, amount: number): Rgb => [
    from[0] + (to[0] - from[0]) * amount,
    from[1] + (to[1] - from[1]) * amount,
    from[2] + (to[2] - from[2]) * amount
]

export const sampleStops = (stops: ColorStop[], t: number): Rgb => {
    const clamped = Math.min(Math.max(t, 0), 1)

    for (let index = 1; index < stops.length; index++) {
        const [offset, color] = stops[index]

        if (clamped <= offset) {
            const [previousOffset, previousColor] = stops[index - 1]
            const local = (clamped - previousOffset) / Math.max(offset - previousOffset, 0.0001)

            return mixRgb(hexToRgb(previousColor), hexToRgb(color), local)
        }
    }

    return hexToRgb(stops[stops.length - 1][1])
}
